// Global Instructions Rule Applied!
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { DealCommitmentEntryFields } from '../../../state_manager/data_models/deal_commitment_entry.ts'
import { CompanyTeamEntryFields } from '../../../state_manager/data_models/company_team_entry.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import {
  ACTION_TYPE_INSERT,
  ACTION_TYPE_UPSERT,
  FIELD_KEY_PROG,
  FIELD_KEY_ACTION,
  TABLE_CONTACTS
} from '../../../state_manager/globals.ts'
import type { DealRecord } from '../../../state_manager/data_models/deal_record.ts'

/**
 * Normalize new-deal commitments: ensure each has id and action for submission
 */
export function normalizeNewDealCommitments(commitments: any[]): any[] {
  if (!commitments || !Array.isArray(commitments)) return commitments
  return commitments.map((commitment: any) => {
    const updated = { ...commitment }
    if (!updated[DealCommitmentEntryFields.deal_commitments__id]) {
      updated[DealCommitmentEntryFields.deal_commitments__id] = crypto.randomUUID()
    }
    if (!updated[DealCommitmentEntryFields.prog__deal_commitments__action]) {
      updated[DealCommitmentEntryFields.prog__deal_commitments__action] = ACTION_TYPE_INSERT
    }
    return updated
  })
}

/**
 * Check if a string is a valid UUID format
 */
function isValidUUID(str: any): boolean {
  if (typeof str !== 'string') return false
  // UUID format: 8-4-4-4-12 hexadecimal characters with dashes
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

/**
 * Checks if a contact is new (not an existing contact)
 */
function isNewContact(contact: any): boolean {
  if (!contact) return false
  return contact.value === 'new' || (contact.label && contact.label.includes('(new)'))
}

/**
 * Parse contact name into first_name and last_name
 */
function parseContactName(name: string): { first_name: string | null; last_name: string } {
  if (!name || typeof name !== 'string') {
    return { first_name: null, last_name: '' }
  }

  // Remove "(new)" suffix if present
  const cleanName = name.replace(/\s*\(new\)\s*$/i, '').trim()

  if (!cleanName) {
    return { first_name: null, last_name: '' }
  }

  // Split by whitespace
  const nameParts = cleanName.split(/\s+/).filter((part) => part.length > 0)

  if (nameParts.length === 0) {
    return { first_name: null, last_name: '' }
  }

  if (nameParts.length === 1) {
    // Only one part - use as last_name
    return { first_name: null, last_name: nameParts[0] }
  }

  // Two or more parts - first part is first_name, rest is last_name
  return {
    first_name: nameParts[0],
    last_name: nameParts.slice(1).join(' ')
  }
}

/**
 * Transform contact object to UserEntry format
 * Handles both new and existing contacts, adding appropriate action fields
 */
function transformContactToUserEntry(contact: any): any | null {
  if (!contact) return null

  // Handle case where contact might be a string (shouldn't happen, but be safe)
  if (typeof contact === 'string') {
    return null
  }

  // Check if this is a new contact
  const isNew = isNewContact(contact)

  // Extract contact ID - contact.value should be the UUID from ContactInput
  // Validate it's actually a UUID to catch any edge cases
  let contactId: string | null = null

  if (isNew) {
    // For new contacts, generate a UUID
    contactId = crypto.randomUUID()
  } else {
    // For existing contacts, extract the ID
    if (contact.value && isValidUUID(contact.value)) {
      contactId = contact.value
    } else if (contact[UserEntryFields.contacts__id] && isValidUUID(contact[UserEntryFields.contacts__id])) {
      contactId = contact[UserEntryFields.contacts__id]
    } else if (contact.id && isValidUUID(contact.id)) {
      contactId = contact.id
    } else if (contact.contacts__id && isValidUUID(contact.contacts__id)) {
      contactId = contact.contacts__id
    } else if (
      contact.data?.[UserEntryFields.contacts__id] &&
      isValidUUID(contact.data[UserEntryFields.contacts__id])
    ) {
      contactId = contact.data[UserEntryFields.contacts__id]
    } else if (contact.data?.id && isValidUUID(contact.data.id)) {
      contactId = contact.data.id
    }
  }

  // Extract contact properties - ContactInput provides email directly on contact object
  // Also check contact.data for nested properties
  const contactData = contact.data || {}

  // For new contacts, parse the name from label
  let firstName: string | null = null
  let lastName: string | null = null

  if (isNew) {
    const nameSource = contact.data?.originalSearch || contact.label || ''
    const parsedName = parseContactName(nameSource)
    firstName = parsedName.first_name
    lastName = parsedName.last_name
  } else {
    firstName = contact.first_name || contactData.first_name || contact[UserEntryFields.contacts__first_name] || null
    lastName = contact.last_name || contactData.last_name || contact[UserEntryFields.contacts__last_name] || null
  }

  // Get display name and remove "(new)" suffix if present
  const rawDisplayName =
    contact.label ||
    contact.full_name ||
    contactData.display_name ||
    contact[UserEntryFields.prog__display_name] ||
    null
  const displayName = rawDisplayName ? rawDisplayName.replace(/\s*\(new\)\s*$/i, '').trim() : null

  const userEntry: any = {
    [UserEntryFields.contacts__id]: contactId,
    [UserEntryFields.contacts__email]:
      contact.email || contactData.email || contact[UserEntryFields.contacts__email] || null,
    [UserEntryFields.contacts__first_name]: firstName,
    [UserEntryFields.contacts__last_name]: lastName,
    [UserEntryFields.contacts__preferred_name]:
      contact.preferred_name || contactData.preferred_name || contact[UserEntryFields.contacts__preferred_name] || null,
    [UserEntryFields.prog__display_name]: displayName
  }

  // Add action field for contact creation
  const PROG_CONTACTS_ACTION = `${FIELD_KEY_PROG}${TABLE_CONTACTS}${FIELD_KEY_ACTION}`
  if (isNew) {
    userEntry[PROG_CONTACTS_ACTION] = ACTION_TYPE_UPSERT
  } else {
    userEntry[PROG_CONTACTS_ACTION] = ''
  }

  return userEntry
}

/**
 * Transform commitments array to DealCommitmentEntry format
 */
function transformCommitments(commitments: any[]): any[] {
  if (!commitments) return []
  return commitments
    .filter((commitment) => commitment.fund && commitment.amount)
    .map((commitment) => ({
      [DealCommitmentEntryFields.deal_commitments__fund_id]: commitment.fund,
      [DealCommitmentEntryFields.deal_commitments__amount]: commitment.amount
    }))
}

/**
 * Transform company team entry from API format to CompanyTeamEntrySchema format
 */
export function transformCompanyTeamEntry(entry: any): any {
  if (!entry) return null

  const contacts = entry.contacts || {}

  return {
    [CompanyTeamEntryFields.contacts__id]: entry.contact_id || contacts.id || null,
    [CompanyTeamEntryFields.contacts__email]: contacts.email || null,
    [CompanyTeamEntryFields.contacts__last_name]: contacts.last_name || null,
    [CompanyTeamEntryFields.contacts__first_name]: contacts.first_name || null,
    [CompanyTeamEntryFields.contacts__mobile_phone]: contacts.mobile_phone || null,
    [CompanyTeamEntryFields.contacts__preferred_name]: contacts.preferred_name || null,
    [CompanyTeamEntryFields.contact_accounts__id]: entry.id || null,
    [CompanyTeamEntryFields.contact_accounts__title]: entry.title || null,
    [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: entry.relationship_category_id || null,
    [CompanyTeamEntryFields.contact_accounts__current]: entry.current !== undefined ? entry.current : true, // Default to true if not specified
    [CompanyTeamEntryFields.prog__founder]: entry.founder || false,
    [CompanyTeamEntryFields.prog__display_name]: contacts.display_name || null,
    [CompanyTeamEntryFields.prog__contact_accounts__action]: entry.action || null,
    [CompanyTeamEntryFields.prog__contacts__action]: entry.contact_action || null
  }
}

/**
 * Transform company team array to CompanyTeamEntrySchema format
 */
export function transformCompanyTeam(teamMembers: any[]): any[] {
  if (!teamMembers || !Array.isArray(teamMembers)) return []
  return teamMembers.map(transformCompanyTeamEntry).filter((entry) => entry !== null)
}

/**
 * Transform city field from object to code
 */
function transformCityField(cityValue: any): number | null {
  if (!cityValue) return null

  if (typeof cityValue === 'object') {
    if (cityValue.code) {
      return cityValue.code
    } else if (cityValue.value) {
      return cityValue.value
    }
    return null
  }

  if (cityValue === '' || (typeof cityValue === 'string' && cityValue.trim() === '')) {
    return null
  }

  return cityValue
}

/**
 * Transform account/company field from object to string ID
 */
function transformAccountField(accountValue: any): string | null {
  if (!accountValue) return null

  if (typeof accountValue === 'object') {
    if (accountValue.value) {
      return accountValue.value
    } else if (accountValue.id) {
      return accountValue.id
    }
    return null
  }

  if (accountValue === '' || (typeof accountValue === 'string' && accountValue.trim() === '')) {
    return null
  }

  return typeof accountValue === 'string' ? accountValue : String(accountValue)
}

/**
 * Normalize numeric string fields to numbers
 */
function normalizeNumericFields(dealData: Record<string, any>): Record<string, any> {
  const numericFields = [
    DealFields.deals__allocation_available,
    DealFields.rounds__raise_amount,
    DealFields.rounds__pre_money_valuation,
    DealFields.rounds__post_money_valuation
  ]

  const normalized = { ...dealData }

  numericFields.forEach((field) => {
    if (normalized[field] && typeof normalized[field] === 'string') {
      const parsed = parseFloat(normalized[field])
      normalized[field] = isNaN(parsed) ? null : parsed
    }
  })

  return normalized
}

/**
 * Clean empty fields - convert empty strings to null
 */
function cleanEmptyFields(data: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {}

  Object.keys(data).forEach((key) => {
    const value = data[key]
    if (value === '' || (typeof value === 'string' && value.trim() === '')) {
      cleaned[key] = null
    } else if (value !== undefined) {
      cleaned[key] = value
    }
  })

  return cleaned
}

/**
 * Main transformation function - converts form data to DealRecord format
 */
export function transformFormDataToDealRecord(
  formData: Record<string, any>,
  formValues: Record<string, any>,
  commitments: any[],
  selectedContacts: Record<string, any[]>,
  getTeamMembersFn: (() => any[]) | null,
  isNewAccount: boolean
): DealRecord {
  // Merge formData and formValues
  const allFormValues = { ...formData, ...formValues }

  // Get valid DealFields keys
  const validDealFields = new Set<string>(Object.values(DealFields))

  // Build deal record with only valid DealFields constants
  let dealData: Record<string, any> = {}

  // Only include fields that are in DealFields
  Object.keys(allFormValues).forEach((key) => {
    if (validDealFields.has(key)) {
      dealData[key] = allFormValues[key]
    }
  })

  // Clean empty fields
  dealData = cleanEmptyFields(dealData)

  // Transform account field from object to string ID
  if (dealData[DealFields.deals__account]) {
    dealData[DealFields.deals__account] = transformAccountField(dealData[DealFields.deals__account])
  }

  // Transform city field
  if (dealData[DealFields.accounts__business_city_code]) {
    dealData[DealFields.accounts__business_city_code] = transformCityField(
      dealData[DealFields.accounts__business_city_code]
    )
  }

  // Normalize numeric fields
  dealData = normalizeNumericFields(dealData)

  // Transform commitments
  const transformedCommitments = transformCommitments(commitments)
  dealData[DealFields.prog__deal_commitments] = transformedCommitments

  // Transform company team (only for new companies)
  if (isNewAccount && getTeamMembersFn) {
    dealData[DealFields.prog__company_team] = getTeamMembersFn()
  } else {
    dealData[DealFields.prog__company_team] = []
  }

  // Transform contact relationships
  // Handle both key formats: DealFields constants or simplified keys (introduced_by, sourced_by, deal_team_lead)
  const introducedByContacts =
    selectedContacts[DealFields.contact_accounts__introduced_by] || selectedContacts.introduced_by || []
  const sourcedByContacts =
    selectedContacts[DealFields.contact_accounts__sourced_by] || selectedContacts.sourced_by || []
  const leadContacts = selectedContacts[DealFields.contact_deals__lead] || selectedContacts.deal_team_lead || []

  dealData[DealFields.contact_accounts__introduced_by] = introducedByContacts
    .map(transformContactToUserEntry)
    .filter((entry) => entry !== null)
  dealData[DealFields.contact_accounts__sourced_by] = sourcedByContacts
    .map(transformContactToUserEntry)
    .filter((entry) => entry !== null)
  dealData[DealFields.contact_deals__lead] = leadContacts
    .map(transformContactToUserEntry)
    .filter((entry) => entry !== null)

  return dealData as DealRecord
}
